import { useState, useEffect } from 'react';

/**
 * Hook для отслеживания media query и определения размера экрана
 * @param query CSS media query строка (например, "(min-width: 768px)")
 * @returns boolean - соответствует ли текущий экран медиазапросу
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    // SSR и fallback для случаев когда window недоступен
    if (typeof window === 'undefined') {
      return false;
    }
    
    if (window.matchMedia) {
      return window.matchMedia(query).matches;
    }
    
    return false;
  });

  useEffect(() => {
    // Проверяем доступность window и matchMedia
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    
    // Устанавливаем начальное значение
    setMatches(mediaQuery.matches);

    // Обработчик изменений
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Подписываемся на изменения
    if (mediaQuery.addEventListener) {
      // Современный способ
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Fallback для старых браузеров
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [query]);

  return matches;
};

/**
 * Hook для определения мобильного устройства
 * @returns boolean - true если экран меньше 768px (md breakpoint в Tailwind)
 */
export const useIsMobile = (): boolean => {
  return useMediaQuery('(max-width: 767px)');
};

/**
 * Hook для определения планшетного устройства
 * @returns boolean - true если экран между 768px и 1024px
 */
export const useIsTablet = (): boolean => {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
};

/**
 * Hook для определения десктопного устройства
 * @returns boolean - true если экран больше 1024px
 */
export const useIsDesktop = (): boolean => {
  return useMediaQuery('(min-width: 1024px)');
};

/**
 * Hook для получения текущего breakpoint
 * @returns 'mobile' | 'tablet' | 'desktop'
 */
export const useBreakpoint = (): 'mobile' | 'tablet' | 'desktop' => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  
  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';
  return 'desktop';
};

/**
 * Типы для различных размеров экрана
 */
export type ScreenSize = 'mobile' | 'tablet' | 'desktop';

/**
 * Hook для получения подробной информации о размере экрана
 */
export const useScreenSize = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const breakpoint = useBreakpoint();
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
  };
};